import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Shield, AlertTriangle, Calendar, Upload, ClipboardList, FileText, User as UserIcon, Edit } from "lucide-react";
import { POAdditionalInfo, POComplianceInfo, POComplianceStatus, UpdatePurchaseOrderComplianceParams } from "src/types";
import { RoleHelper, DD_MM_YYYY, isEqualFiles } from "src/helpers";
import { useAuthorisation } from "src/context";
import { CollapsibleSection } from "src/components/ui/collapsible";
import { FileDownloadButton, NoteDisplay } from "src/components";
import {
  DateInputBox,
  TextInputBox,
  TextInputBoxLarge,
  MultiFileUpload,
  SelectInputBox
} from "src/components/ui/inputs";
import { PurchaseOrderProductsSection, PurchaseOrderProductsSectionProps } from "./purchaseOrderProductsSection";
import { PurchaseOrderService } from "src/services";

interface PurchaseOrderComplianceSectionProps extends POComplianceInfo {
  complianceNote?: POAdditionalInfo["complianceNote"];
  purchaseOrderId: string;
  purchaseOrderService: PurchaseOrderService;
  onUpdateCompliance?: (params: UpdatePurchaseOrderComplianceParams) => void;
  isUpdatingCompliance?: boolean;
  complianceUpdateError?: Error | null;
  retryComplianceUpdate?: () => void;
  resetComplianceUpdate?: () => void;
  downloadComplianceDocuments: () => void;
  isFileDownloading?: boolean;
  productsSectionProps: PurchaseOrderProductsSectionProps;
}

type ComplianceFormState = {
  complianceStatus?: POComplianceStatus;
  complianceOfficer: string;
  regulatoryStandard: string;
  riskClassification: POComplianceInfo["riskClassification"] | null;
  msdsRequired: boolean;
  complianceExpiryDate?: string;
  complianceNoteBody: string;
  complianceFiles: File[] | null;
};

const statusOptions: { value: POComplianceStatus; label: string; badgeClass: string }[] = [
  { value: "NOT_REQUIRED", label: "Not Required", badgeClass: "bg-gray-100 text-gray-800" },
  { value: "PENDING", label: "Pending", badgeClass: "bg-warning-100 text-warning-800" },
  { value: "UNDER_REVIEW", label: "Under Review", badgeClass: "bg-primary-100 text-primary-800" },
  { value: "APPROVED", label: "Approved", badgeClass: "bg-success-100 text-success-800" },
  { value: "REJECTED", label: "Rejected", badgeClass: "bg-error-100 text-error-800" }
];

const riskOptions: { value: POComplianceInfo["riskClassification"]; label: string }[] = [
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" }
];

const PurchaseOrderComplianceSection: React.FC<PurchaseOrderComplianceSectionProps> = ({
  complianceStatus,
  complianceOfficer,
  complianceExpiryDate,
  regulatoryStandard,
  riskClassification,
  msdsRequired,
  hasComplianceDocuments,
  complianceDocumentsUpdatedAt,
  complianceNote,
  purchaseOrderId,
  purchaseOrderService,
  onUpdateCompliance,
  isUpdatingCompliance,
  complianceUpdateError,
  retryComplianceUpdate,
  resetComplianceUpdate,
  downloadComplianceDocuments,
  isFileDownloading,
  productsSectionProps
}) => {
  const { userRole } = useAuthorisation();

  const canEditCompliance =
    RoleHelper.isHealfOperationsTeam(userRole) ||
    RoleHelper.isHealfManagementTeam(userRole) ||
    userRole === "HEALF_ACCOUNTANT";

  const buildFormState = useCallback(
    (initialFiles: File[] | null = null): ComplianceFormState => ({
      complianceStatus,
      complianceOfficer: complianceOfficer ?? "",
      regulatoryStandard: regulatoryStandard ?? "",
      riskClassification: riskClassification ?? null,
      msdsRequired: msdsRequired ?? false,
      complianceExpiryDate: complianceExpiryDate ? complianceExpiryDate.split("T")[0] : undefined,
      complianceNoteBody: complianceNote?.body ?? "",
      complianceFiles: initialFiles
    }),
    [
      complianceStatus,
      complianceOfficer,
      regulatoryStandard,
      riskClassification,
      msdsRequired,
      complianceExpiryDate,
      complianceNote?.body
    ]
  );

  const [formState, setFormState] = useState<ComplianceFormState>(buildFormState());
  const [initialComplianceFiles, setInitialComplianceFiles] = useState<File[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Fetch existing compliance files when entering edit mode
  useEffect(() => {
    const fetchExistingFiles = async () => {
      if (isEditing && hasComplianceDocuments) {
        setIsLoadingFiles(true);
        try {
          const existingFiles = await purchaseOrderService.getPurchaseOrderComplianceDocuments(purchaseOrderId);
          setInitialComplianceFiles(existingFiles);
          setFormState((prev) => ({ ...prev, complianceFiles: existingFiles.length > 0 ? existingFiles : null }));
        } catch (error) {
          console.error("Failed to fetch existing compliance files:", error);
          setInitialComplianceFiles([]);
          setFormState((prev) => ({ ...prev, complianceFiles: null }));
        } finally {
          setIsLoadingFiles(false);
        }
      } else if (!isEditing) {
        // Reset when exiting edit mode
        setFormState(buildFormState());
        setInitialComplianceFiles([]);
        resetComplianceUpdate?.();
      }
    };

    fetchExistingFiles();
  }, [isEditing, hasComplianceDocuments, purchaseOrderId, purchaseOrderService, buildFormState, resetComplianceUpdate]);

  useEffect(() => {
    if (hasSubmitted && !isUpdatingCompliance && !complianceUpdateError) {
      setIsEditing(false);
      setHasSubmitted(false);
    }

    if (complianceUpdateError && hasSubmitted) {
      setHasSubmitted(false);
    }
  }, [hasSubmitted, isUpdatingCompliance, complianceUpdateError]);

  const statusMeta = statusOptions.find((option) => option.value === (formState.complianceStatus ?? complianceStatus));

  const hasMetadataChanged = useMemo(() => {
    const filesChanged = !isEqualFiles(formState.complianceFiles ?? [], initialComplianceFiles);

    return (
      formState.complianceStatus !== complianceStatus ||
      formState.complianceOfficer !== (complianceOfficer ?? "") ||
      formState.regulatoryStandard !== (regulatoryStandard ?? "") ||
      (formState.riskClassification ?? null) !== (riskClassification ?? null) ||
      formState.msdsRequired !== (msdsRequired ?? false) ||
      (formState.complianceExpiryDate ?? "") !== (complianceExpiryDate ? complianceExpiryDate.split("T")[0] : "") ||
      formState.complianceNoteBody !== (complianceNote?.body ?? "") ||
      filesChanged
    );
  }, [
    complianceNote?.body,
    complianceOfficer,
    complianceExpiryDate,
    complianceStatus,
    msdsRequired,
    regulatoryStandard,
    riskClassification,
    formState,
    initialComplianceFiles
  ]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!onUpdateCompliance || !hasMetadataChanged) {
      return;
    }

    onUpdateCompliance({
      purchaseOrderId,
      complianceStatus: formState.complianceStatus,
      complianceOfficer: formState.complianceOfficer?.trim() || null,
      regulatoryStandard: formState.regulatoryStandard?.trim() || null,
      riskClassification: formState.riskClassification ?? null,
      msdsRequired: formState.msdsRequired,
      complianceExpiryDate: formState.complianceExpiryDate || null,
      complianceNoteBody: formState.complianceNoteBody?.trim() || null,
      complianceFiles: formState.complianceFiles ?? [],
      initialComplianceFiles: initialComplianceFiles
    });
    setHasSubmitted(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFormState(buildFormState());
    setInitialComplianceFiles([]);
    resetComplianceUpdate?.();
  };

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const formattedExpiryDate = complianceExpiryDate ? DD_MM_YYYY.formatDate(new Date(complianceExpiryDate)) : "Not set";
  const formattedDocumentsUpdatedAt = complianceDocumentsUpdatedAt
    ? DD_MM_YYYY.formatDate(new Date(complianceDocumentsUpdatedAt))
    : undefined;

  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={false}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <Shield className="w-5 h-5 mr-2 text-primary-600" />
            Compliance
          </h3>
        }
      >
        <div className="p-6 space-y-6">
          <CollapsibleSection
            initiallyOpen={false}
            title={<h4 className="text-base font-semibold text-secondary-800">Products linked to this PO</h4>}
          >
            <div className="pt-4">
              <PurchaseOrderProductsSection {...productsSectionProps} />
            </div>
          </CollapsibleSection>

          {!isEditing && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-secondary-200 rounded-lg p-4">
                  <p className="text-xs font-semibold uppercase text-secondary-600 mb-2">Status</p>
                  {statusMeta ? (
                    <span
                      className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full ${statusMeta.badgeClass}`}
                    >
                      {statusMeta.label}
                    </span>
                  ) : (
                    <span className="text-sm text-secondary-700">Not specified</span>
                  )}
                </div>
                <div className="border border-secondary-200 rounded-lg p-4">
                  <p className="text-xs font-semibold uppercase text-secondary-600 mb-2">Compliance Owner</p>
                  <p className="text-sm font-medium text-secondary-900 flex items-center gap-2">
                    <UserIcon className="w-4 h-4 text-secondary-500" />
                    {complianceOfficer || "Not assigned"}
                  </p>
                </div>
                <div className="border border-secondary-200 rounded-lg p-4">
                  <p className="text-xs font-semibold uppercase text-secondary-600 mb-2">Expiry Date</p>
                  <p className="text-sm font-medium text-secondary-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-secondary-500" />
                    {formattedExpiryDate}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-secondary-200 rounded-lg p-4">
                  <p className="text-xs font-semibold uppercase text-secondary-600 mb-2">Regulatory Standard</p>
                  <p className="text-sm font-medium text-secondary-900">{regulatoryStandard || "Not provided"}</p>
                </div>
                <div className="border border-secondary-200 rounded-lg p-4">
                  <p className="text-xs font-semibold uppercase text-secondary-600 mb-2">Risk Classification</p>
                  <p className="text-sm font-medium text-secondary-900">{riskClassification || "Not set"}</p>
                </div>
                <div className="border border-secondary-200 rounded-lg p-4">
                  <p className="text-xs font-semibold uppercase text-secondary-600 mb-2">MSDS Required</p>
                  <p className="text-sm font-medium text-secondary-900">{msdsRequired ? "Yes" : "No"}</p>
                </div>
              </div>

              {complianceNote && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <FileText className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-secondary-800 mb-2">Compliance Note</p>
                      <div className="text-sm text-secondary-700 leading-relaxed">
                        <NoteDisplay note={complianceNote} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="border border-secondary-200 rounded-lg p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase text-secondary-600 mb-1">Compliance Documents</p>
                  <p className="text-sm text-secondary-700">
                    {hasComplianceDocuments
                      ? `Documents available${formattedDocumentsUpdatedAt ? ` • Updated ${formattedDocumentsUpdatedAt}` : ""}`
                      : "No documents uploaded"}
                  </p>
                </div>
                <FileDownloadButton
                  handleDownload={downloadComplianceDocuments}
                  isDisabled={!hasComplianceDocuments || isFileDownloading}
                  buttonText="Download PDFs"
                />
              </div>
            </>
          )}

          {canEditCompliance && !isEditing && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleStartEdit}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit Compliance
              </button>
            </div>
          )}

          {canEditCompliance && onUpdateCompliance && isEditing && (
            <form onSubmit={handleSubmit} className="border border-secondary-200 rounded-xl p-5 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SelectInputBox
                  id="compliance-status"
                  label="Compliance Status"
                  value={formState.complianceStatus ?? ""}
                  onChange={(value) =>
                    setFormState((prev) => ({ ...prev, complianceStatus: value as POComplianceStatus }))
                  }
                  options={statusOptions.map((option) => ({ value: option.value, label: option.label }))}
                  placeholder="Select status"
                />

                <TextInputBox
                  id="compliance-officer"
                  label="Compliance Owner"
                  placeholder="Jane Doe"
                  value={formState.complianceOfficer}
                  onChange={(value) => setFormState((prev) => ({ ...prev, complianceOfficer: value }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInputBox
                  id="regulatory-standard"
                  label="Regulatory Standard"
                  placeholder="e.g. MHRA"
                  value={formState.regulatoryStandard}
                  onChange={(value) => setFormState((prev) => ({ ...prev, regulatoryStandard: value }))}
                />

                <SelectInputBox
                  id="risk-classification"
                  label="Risk Classification"
                  value={formState.riskClassification ?? ""}
                  onChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      riskClassification: (value || null) as POComplianceInfo["riskClassification"]
                    }))
                  }
                  options={[
                    { value: "", label: "Not set" },
                    ...riskOptions.map((option) => ({ value: option.value ?? "", label: option.label }))
                  ]}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div className="flex items-center gap-3 border border-secondary-200 rounded-lg px-4 py-3">
                  <input
                    id="msds-required"
                    type="checkbox"
                    checked={formState.msdsRequired}
                    onChange={(event) => setFormState((prev) => ({ ...prev, msdsRequired: event.target.checked }))}
                    className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="msds-required" className="text-sm font-medium text-secondary-800">
                    MSDS Required
                  </label>
                </div>

                <DateInputBox
                  id="compliance-expiry-date"
                  label="Compliance Expiry Date"
                  value={formState.complianceExpiryDate}
                  onChange={(value) => setFormState((prev) => ({ ...prev, complianceExpiryDate: value || undefined }))}
                />
              </div>

              <TextInputBoxLarge
                id="compliance-note"
                label="Compliance Note"
                placeholder="Add context for the compliance status"
                value={formState.complianceNoteBody}
                onChange={(value) => setFormState((prev) => ({ ...prev, complianceNoteBody: value }))}
                maxLength={1500}
              />

              <div>
                <label className="text-sm font-semibold text-secondary-700 mb-2 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-secondary-500" />
                  Upload Compliance PDFs
                </label>
                {isLoadingFiles ? (
                  <div className="text-sm text-secondary-600 py-4">Loading existing files...</div>
                ) : (
                  <MultiFileUpload
                    uploadText="Select PDF files"
                    uploadedFiles={formState.complianceFiles}
                    onFileChange={(files) => setFormState((prev) => ({ ...prev, complianceFiles: files }))}
                    fileFormat=".pdf,.doc,.docx"
                  />
                )}
              </div>

              {complianceUpdateError && (
                <div className="flex items-center gap-3 bg-error-50 border border-error-200 rounded-lg px-4 py-3 text-sm text-error-800">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>{complianceUpdateError.message}</span>
                  {retryComplianceUpdate && (
                    <button type="button" className="underline font-semibold ml-auto" onClick={retryComplianceUpdate}>
                      Retry
                    </button>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-secondary-700 bg-secondary-100 hover:bg-secondary-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!hasMetadataChanged || isUpdatingCompliance}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:bg-secondary-200 disabled:text-secondary-500 transition-colors"
                >
                  <ClipboardList className="w-4 h-4" />
                  {isUpdatingCompliance ? "Saving..." : "Save Compliance"}
                </button>
              </div>
            </form>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
};

export { PurchaseOrderComplianceSection };
